<template>
  <div id="standard-breakdown" class="qb-breakdown">
    <label class="text-lg text-teal">{{ $t("standard_breakdown") }}</label>
    <DraggableList
      containerClass="teal"
      type="standard-breakdown"
      :data="options"
      :selected="selectedOptions"
      :activeOption="activeOption"
      :isListLoading="isListLoading"
      :searchQuery="searchQuery"
      :onSearch="searchList"
      :onDrag="onDragStandardBreakdown"
      :onDragEnd="onDragEndStandardBreakdown"
      :onSelect="onSelectBreakdown"
      @onRecordEdit="onRecordEdit"
      @onRecordAdd="onRecordAdd"
      @onOpenSettings="onOpenSettings"
      @toggleExpandedOption="(e) => toggleExpandedOption(e)"
    />

    <FormModal
      v-if="showAddEditModal"
      id="edit-record-form"
      title="breakdown.add_edit_header"
      :nameLabel="parentLabel"
      :fields="addEditFields"
      :model="editRecord"
      :showSaveAs="showEditionButtons"
      :showDelete="showEditionButtons"
      addOverflow="true"
      @onSave="saveRecord"
      @onDelete="deleteRecord"
      @closeModal="() => toggleShowAddEditModal(false)"
    />

    <FormModal
      v-if="showSettingsModal && settingsFields"
      :id="modalKey"
      title="breakdown.time_settings_header"
      :subtitle="activeOption.name"
      :nameLabel="parentLabel"
      :fieldsObjects="settingsFields"
      :customValidators="customValidators"
      addOverflow="true"
      @onSave="saveSettings"
      @closeModal="() => toggleShowSettingsModal(false)"
    />

    <StandardBreakdownQuarter
      v-if="showModalQuarter"
      :settings-fields.sync="settingsFields"
      @close-modal="closeModalQuarter"
      @on-save="saveSettings"
    />
  </div>
</template>

<script>
import DraggableList from "../draggable_list";
import FormModal from "../../form-modal";
import helperDate from "../../../helpers/date";
import StandardBreakdownQuarter from "./standard_breakdown_quarter";
import { QUERIES } from "../../../config/const";

export default {
  props: {
    options: {
      type: Array,
      required: true,
    },
    isListLoading: {
      type: Boolean,
      default: false,
    },
    onDrag: {
      type: Function,
      default: () => null,
    },
    activeOption: {
      type: Object,
      default: () => {},
    },
    activeParentOption: {
      type: Object,
      default: () => {},
    },
    selectedOptions: {
      type: [Array, Number],
      required: true,
    },
    filters: {
      type: Array,
      default: () => [],
    },
  },

  components: {
    DraggableList,
    FormModal,
    StandardBreakdownQuarter,
  },

  data() {
    return {
      modalKey: `modal-key${new Date()}`,
      showAddEditModal: false,
      showModalQuarter: false,
      editRecord: {},
      showSettingsModal: false,
      settingsFields: {},
      searchQuery: "",
      customValidators: {},
      quarterFullYearLastDate: null,
    };
  },

  computed: {
    showEditionButtons() {
      return Object.keys(this.editRecord).length > 0;
    },
    addEditFields() {
      let fields = {};

      switch (this.parentLabel.toLowerCase()) {
        case "text":
        case "headline":  
          fields = ["name", "search_string"];
          break;
      }

      return fields;
    },

    parentLabel() {
      return this.activeParentOption && this.activeParentOption.name;
    },
  },

  methods: {
    onRecordEdit(option) {
      this.editRecord = option;
      this.$emit("setActiveOption", option);
      this.toggleShowAddEditModal(true);
    },

    onRecordAdd(option) {
      this.$emit("setActiveOption", option);
      this.toggleShowAddEditModal(true);
    },

    saveRecord(recordModel, saveAs) {
      switch (this.parentLabel.toLowerCase()) {
        case "text":
        case "headline":
          if (saveAs) {
            //set active option to parent
            this.$emit("setActiveOption", this.activeParentOption);
          }
          this.$emit("saveBreakdownTextEntry", recordModel, this.parentLabel.toLowerCase());
          this.toggleShowAddEditModal(false);
          break;
      }
    },

    deleteRecord(recordModel, saveAs) {
      this.$emit("deleteBreakdownTextEntry", recordModel);
      this.toggleShowAddEditModal(false);
    },

    searchList(query) {
      this.searchQuery = query;
      this.$emit("getSubOptions", {
        optionId: this.activeParentOption.id,
        query,
      });
    },

    onOpenSettings(option) {
      const data = JSON.parse(JSON.stringify(option));
      this.$emit("setActiveOption", data);
      this.setSettingsFields(data);

      if (data.value === QUERIES.STANDARD_BREAKDOWN.TIME.QUARTER) {
        this.toggleShowModalQuarter(true);
      } else {
        this.toggleShowSettingsModal(true);
      }
    },

    toggleShowModalQuarter(status = true) {
      this.showModalQuarter = status;
    },

    closeModalQuarter() {
      this.toggleShowModalQuarter(false);
    },

    setSettingsFields(option, values) {
      const self = this;

      if (!option.customValidators || !option.customValidators.length)
        option.customValidators = [];

      switch (option.value) {
        case QUERIES.STANDARD_BREAKDOWN.TIME.QUARTER:
          Object.keys(option.settings).forEach((key) => {
            option.settings[key].disabledDate = function (date, values) {
              return self.disableDates(date, values);
            };
            option.settings[key].format = "MMM DD";

            option.settings[key].type = "daymonth";
          });

          this.customValidators.q4 = {
            full_year: {
              validation: this.quarterVerifyHasErroFullYear,
              message: self.quarterFullYearErroMessagem,
            },
          };

          break;

        case QUERIES.STANDARD_BREAKDOWN.TIME.FINANCIAL_YEAR:
          option.settings.start.disabledDate = function (date, values) {
            return self.disableDates(date, values);
          };
          break;

        default:
          this.customValidators = {};
      }

      if (option.value === QUERIES.STANDARD_BREAKDOWN.TIME.QUARTER) {
        Object.keys(option.settings).forEach((key) => {
          option.settings[key].disabledDate = function (date, values) {
            return self.disableDates(date, values);
          };
          option.settings[key].format = "MMM DD";

          option.settings[key].type = "daymonth";
        });

        const self = this;

        this.customValidators.q4 = {
          full_year: {
            validation: this.quarterVerifyHasErroFullYear,
            message: self.quarterFullYearErroMessagem,
          },
        };
      } else {
        this.customValidators = {};
      }

      this.settingsFields = option.settings;
    },

    quarterFullYearErroMessagem() {
      const date = helperDate.dateToMonthYear(this.quarterFullYearLastDate);

      if (this.quarterFullYearErrorExceeds)
        return this.$t("errors.quarter.full_year_exceeds", { date: date });

      return this.$t("errors.quarter.full_year_less", { date: date });
    },

    disableDates(date) {
      const dateStart = new Date();
      const baseDate = new Date();
      const nextYearDate = new Date(
        new Date(baseDate).setUTCFullYear(baseDate.getFullYear() + 1)
      );
      const dateMax = new Date(`${nextYearDate.getFullYear()}-12-31`);
      const dateMin = new Date(`${dateStart.getFullYear()}-01-01 00:00:00`);

      if (date < dateMin || date > dateMax) return true;

      return false;
    },

    saveSettings(settingsModel) {
      this.$emit("saveTimeBreakdownSettings", this.activeOption, settingsModel);
      this.toggleShowSettingsModal(false);
    },

    toggleShowAddEditModal(show) {
      if (!show) {
        this.$emit("setActiveOption", this.activeParentOption);
        this.editRecord = {};
      }

      this.showAddEditModal = show;
    },

    toggleShowSettingsModal(show) {
      if (!show) {
        this.$emit("setActiveOption", this.activeParentOption);
      }

      this.refreshModalKey();
      this.showSettingsModal = show;
    },

    refreshModalKey() {
      this.modalKey = `modal-key${new Date()}`;
    },

    onDragStandardBreakdown(e, value, parentOption) {
      if (!Array.isArray(value)) {
        value = [value];
      }

      const data = value.map((option) => {
        return {
          type: "breakdown",
          breakdown_id: parentOption && parentOption.id,
          breakdown_name: parentOption && parentOption.name,
          parent_breakdown_id: parentOption && parentOption.parent_id,
          hasChildren: option.has_children,
          ...option
        };
      });

      e.dataTransfer.setData("filters", JSON.stringify(data));

      let allowDrag = data[0].draggable_to;

      if (
        parentOption &&
        parentOption.parent_id &&
        allowDrag.indexOf(QUERIES.ALLOW_DRAG.FILTERS) !== -1 &&
        this.filters.find(
          (filter) =>
            filter.parent_breakdown_id === parentOption.parent_id &&
            filter.parent_id !== parentOption.id
        )
      ) {
        allowDrag = allowDrag.filter(
          (drag) => drag !== QUERIES.ALLOW_DRAG.FILTERS
        );
      }

      if (
        Object.keys(data).length > 1 &&
        allowDrag.indexOf(QUERIES.ALLOW_DRAG.EXTERNAL_FILTERS) !== -1
      ) {
        allowDrag = allowDrag.filter(
          (drag) => drag !== QUERIES.ALLOW_DRAG.EXTERNAL_FILTERS
        );
      }
      this.onDrag(allowDrag, data);
    },

    onDragEndStandardBreakdown() {
      this.onDrag(false);
      this.$emit("setSelectedOptions", []);
    },

    onSelectBreakdown({ option, parentOption }) {
      if (!option) {
        return;
      }

      this.$emit("setActiveOption", option);

      if (option && parentOption && parentOption.multi_select) {
        let options = this.selectedOptions;
        const selectedIndex = options.findIndex((opt) => opt.id === option.id);
        if (selectedIndex !== -1) {
          options.splice(selectedIndex, 1);
        } else {
          options.push(option);
        }

        this.$emit("setSelectedOptions", options);
      } else {
        this.$emit("setSelectedOptions", option);
      }
    },

    getOptionById({ optionId, parentId, subOptions }) {
      if (!subOptions) {
        subOptions = this.options;
      }

      let option;
      subOptions.map((opt) => {
        if (!option) {
          let optParentId = opt.parent_id || -1;

          if (
            (parentId && optParentId === parentId && opt.id === optionId) ||
            (!parentId && opt.id === optionId)
          ) {
            option = opt;
          } else if (opt.sub_options && opt.sub_options.length > 0) {
            option = this.getOptionById({
              optionId,
              parentId,
              subOptions: opt.sub_options,
            });
          }
        }
      });
      return option;
    },

    toggleExpandedOption(e) {
      this.searchQuery = "";
      this.$emit("toggleExpandedOption", e);
    },

    quarterVerifyHasErroFullYear(settingsModel) {
      const model = Object.values(settingsModel);
      const firstDate = new Date(`${model[0][0]}`);
      const lastDate = new Date(`${model[model.length - 1][1]}`);
      const onYearLate = new Date(
        firstDate.setMonth(firstDate.getMonth() + 12)
      );
      const onYearLateLessOneDay = new Date(
        onYearLate.setDate(firstDate.getDate() - 1)
      );
      let data = {
        hasError: false,
        longer_period: true,
      };

      if (
        lastDate.getDate() === onYearLateLessOneDay.getDate() &&
        lastDate.getMonth() === onYearLateLessOneDay.getMonth() &&
        lastDate.getFullYear() === onYearLateLessOneDay.getFullYear()
      )
        return false;

      this.quarterFullYearLastDate = onYearLateLessOneDay;

      if (lastDate.getTime() > onYearLateLessOneDay.getTime())
        this.quarterFullYearErrorExceeds = true;

      return true;
    },
  },
};
</script>